/**
 * useChallengeCreation Hook
 * Handles challenge creation API calls
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { router } from 'expo-router';
import { API_URL } from '../../../constants/api';
import { logger } from '../../helpers/logger.helper';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { useDialogStore } from '../../shared/stores/dialogStore';
import { buildPublicSignInHref } from '../../signIn/helpers/postAuthRedirect.helper';
import { generateChallengeName } from '../helpers/createDataBuilders';
import {
  resolveFriendUserId,
  validateChallengeInputs,
  buildGroupParticipantIds,
  buildFriendParticipantIds,
  buildChallengeData,
  buildPicksData,
  extractMatchIds,
} from '../helpers/createChallenge.helper';
import { hasEnoughCoins } from '../../shared/helpers/coins.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import type { PickSlip, Game, Friend } from '../types/create.types';
import type { Group } from '../../../types/challenges';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { OPEN_CHALLENGE_PARTICIPANT_ID } from '../constants/create.constants';

const buildAuthHeaders = (token: string | null | undefined) => ({
  'Content-Type': 'application/json',
  ...(token && { Authorization: `Bearer ${token}` }),
});

const POST_CHALLENGE_TIMEOUT_MS = 15000;
const POST_CHALLENGE_COLD_START_RETRY_DELAY_MS = 400;

/** Cold-start "Network Error" on Android: first outbound HTTPS after launch can fail at DNS/TLS warm-up. Retry once. */
const isColdStartNetworkError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return false;
  return error.message === 'Network Error' || error.code === 'ERR_NETWORK';
};

const postChallenge = async (challengeData: unknown, token: string | null) => {
  const config = {
    headers: buildAuthHeaders(token),
    timeout: POST_CHALLENGE_TIMEOUT_MS,
  };
  try {
    const response = await axios.post<{ success: boolean; data?: unknown }>(
      `${API_URL}/challenges`,
      challengeData,
      config
    );
    return response.data;
  } catch (error) {
    if (!isColdStartNetworkError(error)) throw error;
    await new Promise((resolve) => setTimeout(resolve, POST_CHALLENGE_COLD_START_RETRY_DELAY_MS));
    const response = await axios.post<{ success: boolean; data?: unknown }>(
      `${API_URL}/challenges`,
      challengeData,
      config
    );
    return response.data;
  }
};

interface UseChallengeCreationProps {
  getToken?: () => Promise<string | null>;
  getUserDisplayName: () => string | null;
}

interface UseChallengeCreationReturn {
  handleSendChallenge: (
    pickSlips: PickSlip[],
    games: Game[],
    selectedFriendId: string | null,
    selectedGroupId: string | null,
    selectedGroup: Group | null,
    friends: Friend[],
    currentUserId: string,
    onSuccess: () => void,
    onRollback?: () => void,
    stakeCoins?: number,
    maxGames?: number
  ) => Promise<void>;
}

export const useChallengeCreation = ({
  getToken,
}: UseChallengeCreationProps): UseChallengeCreationReturn => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const invalidateXp = useInvalidateXpSummary();

  const handleSendChallenge = useCallback(
    async (
      pickSlips: PickSlip[],
      games: Game[],
      selectedFriendId: string | null,
      selectedGroupId: string | null,
      selectedGroup: Group | null,
      friends: Friend[],
      currentUserId: string,
      onSuccess: () => void,
      onRollback?: () => void,
      stakeCoins?: number,
      maxGames?: number
    ) => {
      // Open challenge (public board): the reserved OPEN id means "post to anyone" — there is no
      // named opponent, so build the open body and return before any friend/group resolution.
      if (selectedFriendId === OPEN_CHALLENGE_PARTICIPANT_ID) {
        const openValidation = validateChallengeInputs(null, null, pickSlips, undefined, null, maxGames, true);
        if (!openValidation.valid) {
          if (openValidation.error) showSnackbar(openValidation.error, 'error');
          onRollback?.();
          return;
        }
        try {
          const cleanUserId = typeof currentUserId === 'string' ? currentUserId.trim() : currentUserId;
          const matchIds = [...new Set(extractMatchIds(pickSlips))];
          const picks = buildPicksData(pickSlips, games);
          const challengeName = generateChallengeName(games, pickSlips, []);
          const challengeData = buildChallengeData(
            cleanUserId,
            challengeName,
            [], // no named participants — the first taker fills the slot
            matchIds,
            undefined,
            undefined,
            picks.length > 0 ? picks : undefined,
            stakeCoins,
            true // isOpen
          );
          const token = getToken ? await getToken() : null;
          await postChallenge(challengeData, token);
          queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] });
          queryClient.invalidateQueries({ queryKey: ['challenges', 'open-board'] });
          if ((stakeCoins ?? 0) > 0) {
            useEconomyStore.getState().syncCoins(useEconomyStore.getState().coins - (stakeCoins ?? 0));
          }
          onSuccess();
        } catch (error: unknown) {
          const axiosErr = axios.isAxiosError(error) ? error : null;
          const responseData = axiosErr?.response?.data as Record<string, unknown> | undefined;
          const errorMessage =
            (responseData?.details as string | undefined) ||
            (responseData?.error as string | undefined) ||
            (error instanceof Error ? error.message : undefined) ||
            'Failed to post open challenge. Please try again.';
          logger.error('[useChallengeCreation] Open challenge creation failed', { error: errorMessage });
          showSnackbar(errorMessage, 'error');
          onRollback?.();
        }
        return;
      }

      // Validate inputs
      const friend = selectedFriendId ? friends.find((f) => f.id === selectedFriendId) : undefined;

      // Prevent self-challenge (should not happen if UI excludes current user; defense in depth)
      if (selectedFriendId && currentUserId && selectedFriendId === currentUserId) {
        showSnackbar('You cannot create a challenge with yourself. Please select a friend.', 'error');
        onRollback?.();
        return;
      }

      const validation = validateChallengeInputs(selectedFriendId, selectedGroupId, pickSlips, friend, selectedFriendId, maxGames);

      if (!validation.valid) {
        if (validation.error) {
          logger.error('[useChallengeCreation] Validation failed', { error: validation.error });
          showSnackbar(validation.error, 'error');
        }
        onRollback?.();
        return;
      }

      if (
        selectedFriendId &&
        friend &&
        typeof friend.coins === 'number' &&
        !hasEnoughCoins(friend.coins, stakeCoins)
      ) {
        showSnackbar(
          `${friend.name} does not have enough coins for this challenge.`,
          'error'
        );
        onRollback?.();
        return;
      }

      const selectedFriendsList: Friend[] = [];
      const nonRegisteredEmails: string[] = [];

      if (selectedFriendId === MONKEY_USER_ID && !friend) {
        selectedFriendsList.push({
          id: MONKEY_USER_ID,
          name: 'Monkey',
          avatar: '',
        });
      }

      if (selectedFriendId && friend) {
        // Resolve friend user ID using helper
        const resolved = resolveFriendUserId(friend, selectedFriendId);

        // Guard clause: Check for errors before proceeding
        if (resolved.error) {
          logger.error('[useChallengeCreation] Friend resolution failed', { message: resolved.error.message });
          showSnackbar(resolved.error.message, 'error');
          onRollback?.();
          return;
        }

        if (resolved.isNonRegistered && resolved.email) {
          // User might not be registered, or inviteeId might be null
          // Send email to server - it will check if user exists and resolve to userId if found
          // If user doesn't exist, server will return an error (challenges only support registered users with userIds)
          nonRegisteredEmails.push(resolved.email);
        } else if (resolved.userId) {
          // Add friend with resolved user ID
          selectedFriendsList.push({
            ...friend,
            id: resolved.userId,
          });
        }
      }

      // Note: nonRegisteredEmails will be sent to backend as participantEmails

      try {
        const cleanUserId = typeof currentUserId === 'string' ? currentUserId.trim() : currentUserId;
        const matchIds = [...new Set(extractMatchIds(pickSlips))];
        const picks = buildPicksData(pickSlips, games);

        // Determine challenge name base
        let challengeNameBase: string;
        let groupId: string | undefined;

        if (selectedGroupId && selectedGroup) {
          // Group challenge: use group name as challenge name
          challengeNameBase = selectedGroup.name;
          groupId = selectedGroup.id;

          const participantIds = buildGroupParticipantIds(cleanUserId, selectedGroup);

          const challengeData = buildChallengeData(
            cleanUserId,
            challengeNameBase,
            participantIds,
            matchIds,
            groupId,
            nonRegisteredEmails.length > 0 ? nonRegisteredEmails : undefined,
            picks.length > 0 ? picks : undefined,
            stakeCoins
          );

          const token = getToken ? await getToken() : null;
          await postChallenge(challengeData, token);
        } else {
          // Create a separate challenge for EACH friend
          if (selectedFriendsList.length === 0 && nonRegisteredEmails.length > 0) {
            const challengeName = generateChallengeName(games, pickSlips, []);

            const challengeData = buildChallengeData(
              cleanUserId,
              challengeName,
              [cleanUserId],
              matchIds,
              undefined,
              nonRegisteredEmails,
              picks.length > 0 ? picks : undefined,
              stakeCoins
            );

            const token = getToken ? await getToken() : null;
            await postChallenge(challengeData, token);
          } else {
            for (const friend of selectedFriendsList) {
              if (friend.id.startsWith('pending_')) {
                logger.error('Attempted to create challenge with pending ID', { friendId: friend.id });
                const errorMsg = `Cannot create challenge with ${friend.name} - user information is incomplete. Please refresh and try again.`;
                logger.error('[useChallengeCreation]', { message: errorMsg });
                showSnackbar(errorMsg, 'error');
                continue;
              }

              const participantIds = buildFriendParticipantIds(cleanUserId, friend.id);
              const challengeName = generateChallengeName(games, pickSlips, [friend]);

              const challengeData = buildChallengeData(
                cleanUserId,
                challengeName,
                participantIds,
                matchIds,
                undefined,
                nonRegisteredEmails.length > 0 ? nonRegisteredEmails : undefined,
                picks.length > 0 ? picks : undefined,
                stakeCoins
              );

              const token = getToken ? await getToken() : null;
              await postChallenge(challengeData, token);
            }
          }
        }

        // Invalidate only the active list (same key as Arena/Friends). Avoid ['challenges'] so we
        // don't refetch other challenges queries (e.g. history, group-leaderboard) or trigger a second GET.
        queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] });
        if (groupId) {
          queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'challenges'] });
          queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'challenges', 'joinable'] });
          queryClient.invalidateQueries({ queryKey: ['groups', 'dashboard', groupId] });
        }
        // Invalidate friend stats so 1v1 history screen shows the new challenge without refresh
        queryClient.invalidateQueries({ queryKey: ['friendStats'] });

        // Deduct stake coins immediately so balance updates without reload
        if ((stakeCoins ?? 0) > 0) {
          useEconomyStore.getState().syncCoins(useEconomyStore.getState().coins - (stakeCoins ?? 0));
        }

        // Sending awards XP server-side; refetch so the header/"+X XP" flash surfaces now.
        invalidateXp();

        // Challenge created successfully
        onSuccess();
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.status === 403 && (error.response?.data as Record<string, unknown>)?.code === 'ANONYMOUS_USER') {
          void useDialogStore.getState().showDialog({
            title: 'Sign up to continue',
            message: 'Create a free account to challenge friends or groups.',
            confirmText: 'Sign Up',
            cancelText: 'Not now',
          }).then((confirmed) => {
            if (confirmed) {
              router.replace(buildPublicSignInHref({ signup: true }) as `/${string}`);
            }
          });
          onRollback?.();
          return;
        }

        // Extract error message from API response
        const axiosErr = axios.isAxiosError(error) ? error : null;
        const responseData = axiosErr?.response?.data as Record<string, unknown> | undefined;
        const errorMessage =
          (responseData?.details as string | undefined) ||
          (responseData?.error as string | undefined) ||
          (error instanceof Error ? error.message : undefined) ||
          'Failed to create challenge. Please try again.';
        logger.error('[useChallengeCreation] Challenge creation failed', {
          error: errorMessage,
          response: responseData,
        });
        showSnackbar(errorMessage, 'error');
        onRollback?.();
      }
    },
    [getToken, queryClient, showSnackbar, invalidateXp]
  );

  return {
    handleSendChallenge,
  };
};
